/**
 * arbi listen [--agent <name>]
 *
 * Start the DM listener, optionally setting the agent backend.
 *
 *   arbi listen --agent claude    Set backend to claude + start listening
 *   arbi listen                   Start listening with previously configured backend
 *
 * Authentication must be completed first via `arbi login`.
 */

import { existsSync, mkdirSync, readFileSync, writeFileSync } from 'node:fs'
import { execFileSync } from 'node:child_process'
import { resolve, dirname, join } from 'node:path'
import { homedir } from 'node:os'
import { fileURLToPath } from 'node:url'
import { Command } from 'commander'
import { startDmListener } from '@arbidocs/sdk'
import { ClaudeOrchestrator, OpenClawOrchestrator, type Orchestrator } from '@arbidocs/sdk'
import { resolveDmCrypto } from '../helpers.js'
import { store, resolveConfig, updateConfig } from '../config.js'
import { error as cliError, success, chalk, dim } from '../output.js'

const AGENT_BACKENDS = ['claude', 'openclaw'] as const
type AgentBackend = (typeof AGENT_BACKENDS)[number]

const AGENT_MIN_VERSIONS: Record<
  AgentBackend,
  { binary: string; minVersion: string; installUrl: string }
> = {
  claude: {
    binary: 'claude',
    minVersion: '2.0.0',
    installUrl: 'https://docs.anthropic.com/en/docs/claude-code',
  },
  openclaw: {
    binary: 'openclaw',
    minVersion: '2026.2.0',
    installUrl: 'https://docs.openclaw.ai/install',
  },
}

/**
 * Compare two dotted version strings ("1.2.3" vs "1.10.0").
 *
 * Returns negative if a < b, positive if a > b, zero if equal. Treats
 * missing components as 0 so "2026" matches "2026.0.0". Exported for
 * unit tests; the runtime caller is {@link checkAgentDependency}.
 */
export function compareVersions(a: string, b: string): number {
  const pa = a.split('.').map(Number)
  const pb = b.split('.').map(Number)
  const len = Math.max(pa.length, pb.length)
  for (let i = 0; i < len; i++) {
    const diff = (pa[i] ?? 0) - (pb[i] ?? 0)
    if (diff !== 0) return diff
  }
  return 0
}

function checkAgentDependency(backend: AgentBackend): void {
  const { binary, minVersion, installUrl } = AGENT_MIN_VERSIONS[backend]
  let version: string
  try {
    version = execFileSync(binary, ['--version'], { encoding: 'utf-8', timeout: 5_000 }).trim()
  } catch {
    cliError(`"${binary}" is not installed.\n  Install: ${installUrl}`)
    process.exit(1)
  }
  const match = version.match(/(\d+\.\d+[\d.]*)/)
  if (match && compareVersions(match[1], minVersion) < 0) {
    cliError(`"${binary}" ${match[1]} is too old (need >= ${minVersion}).\n  Update: ${installUrl}`)
    process.exit(1)
  }
  dim(`${binary} ${match?.[1] ?? version} ✓`)
}

function loadSkillContent(): string | undefined {
  const cliRoot = resolve(dirname(fileURLToPath(import.meta.url)), '..')
  try {
    return readFileSync(join(cliRoot, 'SKILL.md'), 'utf-8')
  } catch {
    return undefined
  }
}

function installSkill(backend: AgentBackend): void {
  const cliRoot = resolve(dirname(fileURLToPath(import.meta.url)), '..')
  let skillContent: string
  try {
    skillContent = readFileSync(join(cliRoot, 'SKILL.md'), 'utf-8')
  } catch {
    return
  }

  if (backend === 'claude') {
    const dir = join(homedir(), '.claude', 'commands', 'arbi')
    const target = join(dir, 'SKILL.md')
    try {
      mkdirSync(dir, { recursive: true })
      if (!existsSync(target) || readFileSync(target, 'utf-8') !== skillContent) {
        writeFileSync(target, skillContent, 'utf-8')
        dim(`Installed ARBI skill → ${target}`)
      }
    } catch {
      /* non-fatal */
    }
  } else if (backend === 'openclaw') {
    const workspace = join(homedir(), '.arbi', 'openclaw-workspace')
    const bootstrap = join(workspace, 'BOOTSTRAP.md')
    try {
      mkdirSync(workspace, { recursive: true })
      if (!existsSync(bootstrap) || readFileSync(bootstrap, 'utf-8') !== skillContent) {
        writeFileSync(bootstrap, skillContent, 'utf-8')
        dim(`Installed ARBI skill → ${bootstrap}`)
      }
    } catch {
      /* non-fatal */
    }
  }
}

function createOrchestrator(
  backend: AgentBackend,
  sessionId: string,
  agentId?: string
): Orchestrator {
  if (backend === 'claude')
    return new ClaudeOrchestrator({ sessionId, skillPrompt: loadSkillContent() })
  // OpenClaw provisions one agent per ARBI user (lowercased ext_id); see
  // ARBI's src/core/openclaw_provisioning.py:_agent_id_for_user. Without
  // this the orchestrator falls back to the legacy single-tenant "arbi"
  // agent id which doesn't exist on multi-tenant deployments.
  //
  // ``ARBI_OPENCLAW_HOME``: in multi-tenant deployments ``arbi listen``
  // runs with a per-agent HOME (so the CLI finds .arbi/credentials.json),
  // but openclaw must still resolve its gateway state from the shared real
  // HOME. The supervisor exports this to point at the canonical HOME
  // (e.g. ``/home/node``) so the spawned openclaw subprocess sidesteps
  // the per-agent dir entirely and works without symlinks.
  if (backend === 'openclaw') {
    const spawnHome = process.env.ARBI_OPENCLAW_HOME
    return new OpenClawOrchestrator({ sessionId, agentId, spawnHome })
  }
  throw new Error(`Unknown backend: ${backend}`)
}

export async function setupAgent(
  agentName: string | undefined,
  config: { orchestrator?: string }
): Promise<AgentBackend | undefined> {
  const backend = (agentName ?? config.orchestrator) as AgentBackend | undefined
  if (!backend) return undefined
  if (!AGENT_BACKENDS.includes(backend)) {
    cliError(`Unknown backend: "${backend}". Choose from: ${AGENT_BACKENDS.join(', ')}`)
    process.exit(1)
  }
  checkAgentDependency(backend)
  installSkill(backend)
  if (agentName && agentName !== config.orchestrator) {
    updateConfig({ orchestrator: backend })
    dim(`Default agent backend set to "${backend}"`)
  }
  return backend
}

export async function startListening(
  agentName: string | undefined,
  config: { orchestrator?: string }
): Promise<void> {
  const backend = (agentName ?? config.orchestrator) as AgentBackend | undefined
  if (!backend) {
    cliError(
      `No agent backend configured. Use --agent to specify one:\n  arbi listen --agent claude`
    )
    process.exit(1)
  }

  const creds = store.getCredentials()
  if (!creds?.parentExtId) {
    cliError('DM listener requires a persistent agent identity (parentExtId).')
    process.exit(1)
  }

  dim('Starting DM listener...')
  const { arbi, crypto } = await resolveDmCrypto()
  const fullConfig = resolveConfig()
  const sessionId = arbi.session.getState().userExtId ?? 'default'
  // For multi-tenant openclaw, the openclaw agent id is the listener's
  // own user ext_id (lowercased) — every ARBI user gets a dedicated
  // openclaw agent at registration. Claude doesn't use agentId.
  const agentId = backend === 'openclaw' ? sessionId.toLowerCase() : undefined
  const orchestrator = createOrchestrator(backend, sessionId, agentId)
  if (!arbi.session.getState().accessToken) {
    cliError('No access token — run `arbi login` first.')
    process.exit(1)
  }

  const listener = await startDmListener({
    arbi,
    baseUrl: fullConfig.baseUrl,
    crypto,
    orchestrator,
    parentExtId: creds.parentExtId!,
    onLog: (msg) => dim(msg),
    onError: (msg, err) => cliError(`${msg}${err ? `: ${err}` : ''}`),
  })

  success(`Listening for DMs as ${chalk.cyan(creds.email)} via ${chalk.cyan(backend)}`)
  dim('Press Ctrl+C to stop.')

  const shutdown = () => {
    dim('\nShutting down...')
    listener.close()
    orchestrator.close?.()
    process.exit(0)
  }
  process.on('SIGINT', shutdown)
  process.on('SIGTERM', shutdown)
  await new Promise(() => {})
}

export function registerListenCommand(program: Command): void {
  program
    .command('listen')
    .description(
      'Start DM listener (foreground; runs until Ctrl-C — use `& disown` or systemd for background)'
    )
    .option('--agent <name>', `Agent backend: ${AGENT_BACKENDS.join(', ')}`)
    .action((opts: { agent?: string }) =>
      (async () => {
        const config = resolveConfig()
        await setupAgent(opts.agent, config)
        await startListening(opts.agent, config)
      })().catch((err) => {
        cliError(`Error: ${err instanceof Error ? err.message : String(err)}`)
        process.exit(1)
      })
    )
}
